import React from "react";
import { useTranslation } from "react-i18next";
import { UsersApiParams } from "../types/user";
import { useUserRoles } from "../api/users";

interface UsersFiltersProps {
    filters: UsersApiParams;
    onFiltersChange: (filters: UsersApiParams) => void;
}

export const UsersFilters: React.FC<UsersFiltersProps> = ({
    filters,
    onFiltersChange,
}) => {
    const { t } = useTranslation("users");
    const { data: roles, isLoading: rolesLoading } = useUserRoles() as {
        data: { id: number; name: string; guard_name: string }[] | undefined;
        isLoading: boolean;
    };

    const handleFilterChange = (key: keyof UsersApiParams, value: any) => {
        onFiltersChange({
            ...filters,
            [key]: value,
            page: 1, // Сбрасываем страницу при изменении фильтров
        });
    };

    return (
        <div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                {t("filters.title")}
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {/* Поиск */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t("filters.search")}
                    </label>
                    <input
                        type="text"
                        value={filters.search || ""}
                        onChange={(e) =>
                            handleFilterChange("search", e.target.value)
                        }
                        placeholder={t("filters.searchPlaceholder")}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                </div>

                {/* Роль */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t("filters.role")}
                    </label>
                    <select
                        value={filters.role || ""}
                        onChange={(e) =>
                            handleFilterChange(
                                "role",
                                e.target.value || undefined
                            )
                        }
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        disabled={rolesLoading}
                    >
                        <option value="">{t("filters.allRoles")}</option>
                        {rolesLoading ? (
                            <option value="" disabled>
                                {t("loading")}
                            </option>
                        ) : (
                            roles?.map((role) => (
                                <option key={role.id} value={role.name}>
                                    {role.name}
                                </option>
                            ))
                        )}
                    </select>
                </div>

                {/* Статус доступа (access_status) */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t("filters.blocked")}
                    </label>
                    <select
                        value={
                            filters.access_status === undefined
                                ? ""
                                : filters.access_status.toString()
                        }
                        onChange={(e) => {
                            const value = e.target.value;
                            handleFilterChange(
                                "access_status",
                                value === "" ? undefined : value
                            );
                        }}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                        <option value="">{t("filters.allStatuses")}</option>
                        <option value="0">{t("filters.active")}</option>
                        <option value="1">{t("filters.blockedOption")}</option>
                        <option value="2">{t("filters.restricted")}</option>
                        <option value="3">Удаленный</option>
                    </select>
                </div>

                {/* Тип аккаунта (account_type) */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Тип аккаунта
                    </label>
                    <select
                        value={filters.account_type || ""}
                        onChange={(e) =>
                            handleFilterChange(
                                "account_type",
                                e.target.value || undefined
                            )
                        }
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                        <option value="">Все типы</option>
                        <option value="0">Клиент</option>
                        <option value="1">Бизнес-менеджер</option>
                        <option value="2">Контрактный сотрудник</option>
                        <option value="3">Из игры (гость)</option>
                        <option value="4">Из игры (VIP)</option>
                    </select>
                </div>

                {/* Страна */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t("filters.country")}
                    </label>
                    <input
                        type="text"
                        value={filters.country || ""}
                        onChange={(e) =>
                            handleFilterChange("country", e.target.value)
                        }
                        placeholder={t("filters.countryPlaceholder")}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                </div>

                {/* Пол */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t("filters.sex")}
                    </label>
                    <select
                        value={filters.sex || ""}
                        onChange={(e) =>
                            handleFilterChange(
                                "sex",
                                e.target.value || undefined
                            )
                        }
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                        <option value="">{t("filters.allGenders")}</option>
                        <option value="m">{t("filters.male")}</option>
                        <option value="f">{t("filters.female")}</option>
                        <option value="other">{t("filters.other")}</option>
                    </select>
                </div>

                {/* Фильтр по дате следующей оплаты */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t("filters.memberFee")}
                    </label>
                    <select
                        value={filters.member_fee_filter || ""}
                        onChange={(e) =>
                            handleFilterChange(
                                "member_fee_filter",
                                e.target.value || undefined
                            )
                        }
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                        <option value="">{t("filters.memberFeeAll")}</option>
                        <option value="paid">
                            {t("filters.memberFeePaid", "Оплачено")}
                        </option>
                        <option value="overdue">
                            {t("filters.memberFeeOverdue")}
                        </option>
                        <option value="1d">
                            {t("filters.memberFee1Day")}
                        </option>
                        <option value="2d">
                            {t("filters.memberFee2Days")}
                        </option>
                        <option value="3d">
                            {t("filters.memberFee3Days")}
                        </option>
                        <option value="1w">
                            {t("filters.memberFee1Week")}
                        </option>
                        <option value="2w">
                            {t("filters.memberFee2Weeks")}
                        </option>
                        <option value="1m">
                            {t("filters.memberFee1Month")}
                        </option>
                    </select>
                </div>

                {/* Элементов на странице */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t("filters.perPage")}
                    </label>
                    <select
                        value={filters.per_page || 20}
                        onChange={(e) =>
                            handleFilterChange(
                                "per_page",
                                parseInt(e.target.value)
                            )
                        }
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                    </select>
                </div>
            </div>

            {/* Кнопки */}
            <div className="flex justify-end space-x-2 mt-4">
                <button
                    onClick={() => onFiltersChange({})}
                    className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600"
                >
                    {t("filters.clear")}
                </button>
            </div>
        </div>
    );
};
